import { useState, type ReactNode } from "react";
import { BRAND, LANDING_NAV, TRACKS } from "../design/copy.js";
import { BloomBackground } from "../ui/background.js";
import { MarkerItem, Rule } from "../ui/primitives.js";
import type { RouteName } from "../routes.js";

/**
 * Frames 1, 2 and 3 — one screen in three states.
 *
 * Frame 2 is frame 1 with `scenarios.` hovered: the marker appears AND the
 * list opens up around the hovered item. Frame 3 is frame 2 with the track
 * list expanded in place between `scenarios.` and `calibration.`, pushing
 * the rest of the nav down. Neither is a separate page.
 */
export function Landing({
  onNavigate,
  onOpenSettings,
  startExpanded = false,
}: {
  onNavigate: (r: RouteName) => void;
  onOpenSettings: () => void;
  startExpanded?: boolean | undefined;
}): ReactNode {
  const [expanded, setExpanded] = useState(startExpanded);

  const activate = (id: string): void => {
    if (id === "scenarios") {
      setExpanded((v) => !v);
      return;
    }
    if (id === "start") onNavigate("auth");
    if (id === "calibration") {
      setExpanded(true);
      onNavigate("scenarios");
    }
    if (id === "settings") onOpenSettings();
  };

  return (
    <div className="relative flex min-h-svh">
      <div className="relative z-10 flex flex-col bg-paper" style={{ width: "var(--pc-rail)" }}>
        <div className="flex-1 pt-16" style={{ paddingLeft: "var(--pc-gutter)", paddingRight: "var(--pc-space-16)" }}>
          <div className="font-sans text-body">{BRAND.eyebrow}</div>
          <Rule className="mt-2" />
          <h1 className="mt-6 mb-0 font-display text-display font-bold">
            {BRAND.wordmarkLine1}
            <br />
            {BRAND.wordmarkLine2}
          </h1>
          <p className="mt-10 mb-0 font-sans text-body">
            {BRAND.taglineLine1}
            <br />
            {BRAND.taglineLine2}
          </p>
          <Rule className="mt-4" />

          <ul className="mt-10 list-none p-0 m-0">
            {LANDING_NAV.map((item) => (
              <li key={item.id}>
                <MarkerItem
                  onClick={item.id === "events" ? undefined : () => activate(item.id)}
                  disabled={item.id === "events"}
                  active={item.id === "scenarios" && expanded}
                >
                  {item.label}
                </MarkerItem>
                {item.id === "scenarios" && expanded ? <TrackList onPick={() => onNavigate("auth")} /> : null}
              </li>
            ))}
          </ul>
        </div>

        <div className="pb-8" style={{ paddingLeft: "var(--pc-gutter)" }}>
          <button
            type="button"
            onClick={() => onNavigate("auth")}
            className="block cursor-pointer border-0 bg-transparent p-0 font-display text-micro font-bold text-ink hover:bg-marker"
          >
            {BRAND.bullets} {BRAND.logIn}
          </button>
          <div className="mt-3 font-sans text-micro">{BRAND.copyright}</div>
        </div>
      </div>

      <div className="relative flex-1">
        <BloomBackground />
      </div>
    </div>
  );
}

/**
 * Frame 3. Only Calibration is unlocked; the frames show no lock affordance
 * at all, so a locked track is simply faint and unfocusable — see
 * docs/DESIGN-SYSTEM.md ambiguity A2. No padlock, no explanation.
 */
export function TrackList({ onPick }: { onPick: () => void }): ReactNode {
  return (
    <ul className="mt-4 mb-8 list-none p-0 m-0">
      {TRACKS.map((track) => {
        const unlocked = track.key === "CALIBRATION";
        return (
          <li key={track.key} className="leading-[1.75]">
            {unlocked ? (
              <button
                type="button"
                onClick={onPick}
                className="cursor-pointer border-0 bg-transparent p-0 font-display text-micro font-bold text-ink hover:bg-marker"
              >
                {track.label}
              </button>
            ) : (
              <span aria-disabled="true" className="font-display text-micro font-bold text-ink-faint select-none">
                {track.label}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}
